import { useEffect, useState } from "react";
import { Leaf, Loader2, ShieldCheck } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useLogin } from "@/hooks/useLogin";
import { useLogout } from "@/hooks/useLogout";

/**
 * /login — split-pane: brand panel on the left (gradient + tagline),
 * form on the right. Same behaviour as before — POSTs /api/v1/auth/login/
 * after fetching a CSRF cookie, redirects to ?next= on success.
 */
export default function Login() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { data: user, isLoading: meLoading } = useCurrentUser();
  const login = useLogin();
  const logout = useLogout();

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");

  // ?logout=1 from the Django public-portal "Sign Out" link.
  useEffect(() => {
    if (search.get("logout") === "1") {
      logout.mutate(undefined, {
        onSettled: () => navigate("/login", { replace: true }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Already signed in — send them on.
  useEffect(() => {
    if (user) navigate(search.get("next") || "/", { replace: true });
  }, [user, navigate, search]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    login.mutate(
      { email, password },
      { onSuccess: () => navigate(search.get("next") || "/", { replace: true }) },
    );
  }

  if (meLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const err = login.error as
    | { response?: { data?: { detail?: string }; status?: number } }
    | undefined;
  const errorMsg = err?.response?.data?.detail
    ?? (err && (err.response?.status === 429
      ? "Too many sign-in attempts. Try again in a minute."
      : err.response?.status ? "Couldn't sign in." : undefined));

  return (
    <div className="grid min-h-screen w-screen lg:grid-cols-2">

      {/* ----- Brand panel (left) ----- */}
      <div className="relative hidden overflow-hidden bg-gradient-to-br from-primary via-emerald-800 to-emerald-950 lg:flex lg:flex-col lg:justify-between lg:p-12 lg:text-primary-foreground">
        {/* Decorative SVG dot grid — same trick the public hero uses,
            pure CSS so no extra asset to ship. */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='2' cy='2' r='1.5' fill='%23ffffff'/%3E%3C/svg%3E\")",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-card/15 backdrop-blur">
              <Leaf className="h-5 w-5" />
            </div>
            <div className="text-sm font-semibold tracking-tight">Climate KMS</div>
          </div>
        </div>

        <div className="relative space-y-6">
          <h1 className="text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
            Ethiopia&apos;s climate knowledge,<br/>in one place.
          </h1>
          <p className="max-w-md text-sm leading-relaxed text-white/80">
            Track indicators, publish policy briefs, and coordinate adaptation
            programs across the Ministry of Planning and Development.
          </p>
          <div className="flex items-center gap-2 text-xs text-white/60">
            <ShieldCheck className="h-4 w-4" />
            <span>Federal Democratic Republic of Ethiopia</span>
          </div>
        </div>

        <div className="relative text-xs text-white/40">
          © {new Date().getFullYear()} Ministry of Planning &amp; Development
        </div>
      </div>

      {/* ----- Form panel (right) ----- */}
      <div className="flex items-center justify-center px-6 py-12 sm:px-12">
        <div className="w-full max-w-sm space-y-6">
          {/* Brand row — visible only when the left panel is collapsed. */}
          <div className="flex items-center gap-3 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-emerald-700 text-primary-foreground">
              <Leaf className="h-4 w-4" />
            </div>
            <span className="text-sm font-semibold">Climate KMS</span>
          </div>

          <div className="space-y-1">
            <h2 className="text-2xl font-bold tracking-tight">Welcome back</h2>
            <p className="text-sm text-muted-foreground">
              Sign in with your ministry credentials.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" name="email"
                autoComplete="email" autoFocus required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@mopd.gov.et"
                disabled={login.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" name="password"
                autoComplete="current-password" required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={login.isPending}
              />
            </div>

            {errorMsg && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {errorMsg}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in…</>
                : "Sign in"}
            </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground">
            Trouble signing in? Contact your department admin.
          </p>
        </div>
      </div>
    </div>
  );
}
